// Emotional check-in (before trading) and check-out (after the close).
import { useI18n } from './i18n';
import { translateNow as tn } from './langState';

export const MOODS_BEFORE = [
  { id: 'Happy', face: '😄' },
  { id: 'Calm', face: '😌' },
  { id: 'Focused', face: '🎯' },
  { id: 'Confident', face: '😎' },
  { id: 'Excited', face: '🤩' },
  { id: 'Tired', face: '😴' },
  { id: 'Anxious', face: '😰' },
  { id: 'Stressed', face: '😣' },
  { id: 'Frustrated', face: '😤' },
  { id: 'Angry', face: '😠' },
  { id: 'Sad', face: '😔' },
  { id: 'Unwell', face: '🤒' },
];

export const MOODS_AFTER = [
  { id: 'Happy', face: '😄' },
  { id: 'Proud', face: '🥳' },
  { id: 'Calm', face: '😌' },
  { id: 'Relieved', face: '😮‍💨' },
  { id: 'Tired', face: '😴' },
  { id: 'Anxious', face: '😰' },
  { id: 'Frustrated', face: '😤' },
  { id: 'Angry', face: '😠' },
  { id: 'Disappointed', face: '😞' },
  { id: 'Sad', face: '😔' },
];

// The emotions that most often cost traders money, each with a reminder for the next session.
export const TRADING_EMOTIONS = [
  { id: 'FOMO', face: '😱', hint: 'Chased a move or jumped in without a setup',
    tip: "Only take setups from your plan. If you missed the move, let it go. There's always another trade." },
  { id: 'Greed', face: '🤑', hint: 'Held too long, moved targets or added size',
    tip: "Take profits at your planned target. Don't move targets or add size because a trade is working." },
  { id: 'Tilt', face: '🌀', hint: 'Lost control after losses',
    tip: 'After 2 losses, step away for 15 minutes. Your daily loss limit is final.' },
  { id: 'Revenge trading', face: '😡', hint: 'Tried to win back a loss',
    tip: "After a loss, wait for your next A+ setup. You don't need to win it back today." },
  { id: 'Fear / hesitation', face: '😨', hint: 'Skipped or cut good setups',
    tip: 'Trust your plan: when your setup appears, take it at your planned size.' },
  { id: 'Overconfidence', face: '😎', hint: 'Sized up or got loose after wins',
    tip: "A big win doesn't change your rules. Keep the same size and the same setups." },
  { id: 'Impatience', face: '⏳', hint: 'Entered before the setup was confirmed',
    tip: 'Wait for price to reach your levels and confirm. No setup, no trade.' },
  { id: 'Boredom / overtrading', face: '🥱', hint: 'Traded just to be trading',
    tip: 'Set a maximum number of trades for today and stop when you hit it.' },
];

const NEGATIVE = ['Tired', 'Anxious', 'Stressed', 'Frustrated', 'Angry', 'Sad', 'Unwell'];

function joinWords(list) {
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} ${tn('and')} ${list[list.length - 1]}`;
}

// How ready the trader is, from their morning check-in. Returns null until they answer something.
export function readiness(day) {
  const answered = day.moodBefore.length || day.sleepHours != null || day.rested || day.energy || day.stress;
  if (!answered) return null;

  const reasons = [];
  let severe = false;
  if (day.sleepHours != null && day.sleepHours < 6) {
    reasons.push(tn(day.sleepHours === 1 ? 'you slept {n} hour' : 'you slept {n} hours').replace('{n}', day.sleepHours));
    if (day.sleepHours < 5) severe = true;
  }
  if (day.rested && day.rested <= 2) reasons.push(tn("you don't feel well rested"));
  if (day.energy && day.energy <= 2) reasons.push(tn('your energy is low'));
  if (day.stress && day.stress >= 4) reasons.push(tn("you're under a lot of stress outside trading"));
  const negative = day.moodBefore.filter((m) => NEGATIVE.includes(m));
  if (negative.length) reasons.push(tn('you feel {moods}').replace('{moods}', joinWords(negative.map((m) => tn(m).toLowerCase()))));
  if (negative.includes('Angry') || negative.includes('Unwell')) severe = true;

  const summary = reasons.length ? `${reasons[0][0].toUpperCase()}${joinWords(reasons).slice(1)}.` : '';
  if (severe || reasons.length >= 3) {
    return { level: 'sitout', title: 'Consider sitting out', text: `${summary} ${tn('Consider sitting out today, or trading your smallest size with a tight daily stop.')}` };
  }
  if (reasons.length) {
    return { level: 'careful', title: 'Trade carefully', text: `${summary} ${tn('Consider smaller size or fewer trades today.')}` };
  }
  return { level: 'ready', title: 'Ready to trade', text: tn("You're rested and in a good headspace. Stick to your plan.") };
}

function FacePicker({ moods, selected, onToggle, label }) {
  const { t } = useI18n();
  return (
    <div className="face-picker" role="group" aria-label={label}>
      {moods.map((m) => {
        const on = selected.includes(m.id);
        return (
          <button key={m.id} type="button" className={`face${on ? ' selected' : ''}${NEGATIVE.includes(m.id) || ['Disappointed'].includes(m.id) ? ' negative' : ''}`}
            aria-pressed={on} onClick={() => onToggle(m.id)}>
            <span className="face-emoji" aria-hidden="true">{m.face}</span>
            <span className="face-label">{t(m.id)}</span>
          </button>
        );
      })}
    </div>
  );
}

// A 1–5 scale with words at each end. Clicking the selected value clears it.
function Scale({ label, value, onChange, low, high, reverse }) {
  return (
    <div className="scale">
      <span className="scale-label">{label}</span>
      <div className="scale-row">
        <span className="scale-end">{low}</span>
        <div className="scale-buttons" role="radiogroup" aria-label={label}>
          {[1, 2, 3, 4, 5].map((n) => {
            const good = reverse ? n <= 2 : n >= 4;
            const bad = reverse ? n >= 4 : n <= 2;
            return (
              <button key={n} type="button" role="radio" aria-checked={value === n}
                className={`scale-button${value === n ? ' selected' : ''}${good ? ' good' : bad ? ' bad' : ''}`}
                onClick={() => onChange(value === n ? null : n)}>
                {n}
              </button>
            );
          })}
        </div>
        <span className="scale-end">{high}</span>
      </div>
    </div>
  );
}

function toggleIn(list, id) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

// Check-in answers that point in opposite directions, e.g. "Tired" with energy 5/5.
// Returns a sentence describing the mismatch, or null.
export function checkInConflict(day) {
  const low = (day.moodBefore || []).filter((m) => ['Tired', 'Unwell'].includes(m));
  const high = [];
  if (day.rested >= 4) high.push(tn('your rest {n}/5').replace('{n}', day.rested));
  if (day.energy >= 4) high.push(tn('your energy {n}/5').replace('{n}', day.energy));
  if (low.length && high.length) {
    return tn('You picked {moods}, but rated {ratings}.')
      .replace('{moods}', joinWords(low.map((m) => tn(m).toLowerCase())))
      .replace('{ratings}', joinWords(high));
  }
  if (day.sleepHours != null && day.sleepHours < 5 && day.rested >= 4) {
    return tn('You slept {hours} hours but rated your rest {n}/5.').replace('{hours}', day.sleepHours).replace('{n}', day.rested);
  }
  return null;
}

export function MorningCheckIn({ day, update }) {
  const { t } = useI18n();
  const ready = readiness(day);
  const conflict = day.checkInOk ? null : checkInConflict(day);
  return (
    <div className="routine-block checkin-block">
      <span className="routine-block-title">{t('Morning check-in')}</span>
      <span className="checkin-question">{t('How are you feeling?')}</span>
      <FacePicker moods={MOODS_BEFORE} selected={day.moodBefore} label={t('How are you feeling?')}
        onToggle={(id) => update({ moodBefore: toggleIn(day.moodBefore, id) })} />

      <div className="checkin-grid">
        <label className="sleep-field">
          <span className="scale-label">{t('Hours slept')}</span>
          <span className="sleep-input">
            <input type="number" min="0" max="24" step="0.5" inputMode="decimal"
              value={day.sleepHours ?? ''} placeholder={t('e.g. 7')}
              onChange={(e) => update({ sleepHours: e.target.value === '' ? null : Number(e.target.value) })} />
            <span>{t('hrs')}</span>
          </span>
        </label>
        <Scale label={t('How rested do you feel?')} value={day.rested} low={t('Exhausted')} high={t('Fully rested')}
          onChange={(rested) => update({ rested })} />
        <Scale label={t('Energy')} value={day.energy} low={t('Drained')} high={t('Energized')}
          onChange={(energy) => update({ energy })} />
        <Scale label={t('Stress outside of trading')} value={day.stress} low={t('None')} high={t('A lot')} reverse
          onChange={(stress) => update({ stress })} />
      </div>

      {conflict && (
        <div className="checkin-conflict" role="status">
          <strong>{t('Your answers point in different directions.')}</strong>
          <span>{conflict} {t('If one is off, change it above. If both are right, that’s fine too: mixed days happen.')}</span>
          <button type="button" className="secondary small" onClick={() => update({ checkInOk: true })}>{t('Both are right')}</button>
        </div>
      )}
      {ready && (
        <div className={`readiness readiness-${ready.level}`} role="status">
          <strong>{ready.level === 'ready' ? '✅' : ready.level === 'careful' ? '⚠️' : '🛑'} {t(ready.title)}</strong>
          <span>{ready.text}</span>
        </div>
      )}
    </div>
  );
}

export function EveningCheckOut({ day, update }) {
  const { t } = useI18n();
  return (
    <div className="routine-block checkin-block evening-checkin">
      <span className="routine-block-title">{t('Evening check-out')}</span>
      <span className="checkin-question">{t('How do you feel now?')}</span>
      <FacePicker moods={MOODS_AFTER} selected={day.moodAfter} label={t('How do you feel now?')}
        onToggle={(id) => update({ moodAfter: toggleIn(day.moodAfter, id) })} />
      <div className="impact">
        <span className="scale-label">{t('Did emotions affect your trading today?')}</span>
        <div className="impact-buttons" role="radiogroup" aria-label={t('Did emotions affect your trading today?')}>
          {[['none', 'Not at all'], ['some', 'A little'], ['lots', 'A lot']].map(([id, label]) => (
            <button key={id} type="button" role="radio" aria-checked={day.emotionImpact === id}
              className={`impact-button impact-${id}${day.emotionImpact === id ? ' selected' : ''}`}
              onClick={() => {
                const next = day.emotionImpact === id ? null : id;
                // "Not at all" means nothing took over, so clear any picks.
                update(next === 'some' || next === 'lots' ? { emotionImpact: next } : { emotionImpact: next, emotionsHit: [], emotionTrigger: '' });
              }}>
              {t(label)}
            </button>
          ))}
        </div>
      </div>
      {(day.emotionImpact === 'some' || day.emotionImpact === 'lots') && (
        <div className="emotions-hit">
          <span className="scale-label">{t('Which ones? Tap all that apply.')}</span>
          <div className="emotion-chips" role="group" aria-label={t('Which emotions affected your trading?')}>
            {TRADING_EMOTIONS.map((e) => {
              const on = day.emotionsHit.includes(e.id);
              return (
                <button key={e.id} type="button" className={`emotion-chip${on ? ' selected' : ''}`} aria-pressed={on}
                  onClick={() => update({ emotionsHit: toggleIn(day.emotionsHit, e.id) })}>
                  <span className="emotion-chip-name"><span aria-hidden="true">{e.face}</span> {t(e.id)}</span>
                  <span className="emotion-chip-hint">{t(e.hint)}</span>
                </button>
              );
            })}
          </div>
          {day.emotionsHit.length > 0 && (
            <label className="routine-field">
              <span className="scale-label">{t('What triggered it? (optional)')}</span>
              <input value={day.emotionTrigger} maxLength={500}
                onChange={(e) => update({ emotionTrigger: e.target.value })}
                placeholder={t('e.g. Two quick losses right at the open')} />
            </label>
          )}
        </div>
      )}
      <Scale label={t('Energy now')} value={day.energyAfter} low={t('Drained')} high={t('Energized')}
        onChange={(energyAfter) => update({ energyAfter })} />
    </div>
  );
}
